import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { ChevronLeftIcon } from 'lucide-react';
import {
  Element4,
  SearchNormal1,
  SecuritySafe,
  Fatrows,
  KeySquare,
  ShieldSearch,
  LogoutCurve,
  Setting2,
  Task
} from 'iconsax-react';
import logo from '../../assets/eventus.png';
import logoIcon from '../../assets/eventus_logo.png';

// Dashboard URL from environment variable or default
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL || 'https://devdashboard.eventussecurity.com/dashboard';
const INVESTIGATION_URL = process.env.REACT_APP_INVESTIGATION_URL || 'https://devdashboard.eventussecurity.com/dashboard';
const Threat_Intelligence = process.env.REACT_APP_Threat_Intelligence || 'http://144.24.124.242:8085/rss_feeds';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path?: string;
  externalUrl?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  comingSoon?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
}

const NavItem: React.FC<NavItemProps & { isExpanded?: boolean }> = ({
  icon,
  label,
  path,
  externalUrl,
  isActive = false,
  isDisabled = false,
  comingSoon = false,
  onClick,
  isExpanded = true
}) => {
  if (isDisabled) {
    return (
      <div className="flex items-center px-4 py-3 text-gray-500 cursor-not-allowed opacity-60">
        <span className="mr-3">{icon}</span>
        {isExpanded && <span>{label}</span>}
        {comingSoon && isExpanded && (
          <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            Coming Soon
          </span>
        )}
      </div>
    );
  }

  if (externalUrl) {
    return (
      <a 
        href={externalUrl}
        rel="noopener noreferrer"
        className={`flex items-center px-4 py-3 hover:bg-background-hover rounded-lg transition-colors ${
          isActive ? 'bg-primary/10 text-primary' : 'text-gray-400'
        }`}
        onClick={onClick}
      >
        <span className="mr-3">{icon}</span>
        {isExpanded && <span>{label}</span>}
      </a>
    );
  }

  return (
    <Link
      to={path || '#'}
      className={`flex items-center px-4 py-2.5 hover:bg-background-hover rounded-lg transition-colors ${
        isActive ? 'bg-primary/10 text-primary' : 'text-gray-400'
      }`}
      onClick={onClick}
    >
      <span className="mr-3">{icon}</span>
      {isExpanded && <span>{label}</span>}
    </Link>
  );
};

// Enhanced SidePanel with updated styling
const SidePanel: React.FC<{
  children: React.ReactNode;
  isVisible: boolean;
  topPosition?: number;
}> = ({ children, isVisible, topPosition = 0 }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      className="absolute left-full bg-background-card/50 backdrop-blur-md shadow-lg z-10 rounded-lg overflow-hidden border border-border-dark"
      style={{ top: `${topPosition}px` }}
    >
      <div className="p-2 min-w-[200px]">
        {children}
      </div>
    </div>
  );
};

interface SidebarProps {
  onOpenSettings?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onOpenSettings }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { canAccess, loading: rbacLoading } = useRBAC();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminButtonPosition, setAdminButtonPosition] = useState(0);
  const adminButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  const isLinkActive = (path: string) => {
    // For dashboard we want an exact match
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    // For other paths, we check if the current path starts with the given path
    return location.pathname.startsWith(path);
  };
  
  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    // Close admin panel when sidebar collapses
    if (isExpanded) {
      setShowAdminPanel(false);
    }
  };

  // Handle click outside to close expanded sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close the admin panel when clicking outside, not the entire sidebar
      if (showAdminPanel && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setShowAdminPanel(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdminPanel]); // Only depend on showAdminPanel, not isExpanded

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Toggle the admin panel
  const toggleAdminPanel = (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    // Get the position of the clicked element
    const rect = event.currentTarget.getBoundingClientRect();
    setAdminButtonPosition(rect.top);
    setShowAdminPanel(!showAdminPanel);
    // Prevent default navigation
    event.preventDefault();
    event.stopPropagation(); // Prevent click from closing sidebar
  };
  
  // Handle submenu item click
  const handleSubMenuItemClick = () => {
    setShowAdminPanel(false);
  };
  
  return (
    <div className="relative h-full" ref={sidebarRef}>
      <div className={`h-full flex flex-col bg-background-card transition-all duration-300 ${
        isExpanded ? 'w-64' : 'w-30'
      }`}>
        {/* Logo Section */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center overflow-hidden">
            {isExpanded ? (
              <img src={logo} alt="Eventus Logo" className="h-10 w-auto" />
            ) : (
              <img src={logoIcon} alt="Eventus Logo" className="h-8 w-auto" />
            )}
          </div>
          
          {/* Toggle button */}
          <button 
            onClick={toggleSidebar}
            className="p-1.5 rounded-full hover:bg-background-hover text-gray-400"
            title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <ChevronLeftIcon 
              className={`h-5 w-5 transition-transform duration-300 ${!isExpanded ? 'rotate-180' : ''}`} 
            />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4">
          {/* Main Navigation */}
          <div className="space-y-2 px-2">
            <NavItem 
              icon={<Element4 size={25} color="#426CFF" variant="Bulk" />} 
              label="Dashboard" 
              externalUrl={DASHBOARD_URL}
              isDisabled={true}
              isExpanded={isExpanded}
            />
            
            <NavItem 
              icon={<SearchNormal1 size={25} color="#426CFF" variant="Bulk" />} 
              label="Investigation" 
              externalUrl={INVESTIGATION_URL}
              isDisabled={true}
              isExpanded={isExpanded} 
            />
            
            <NavItem 
              icon={<SecuritySafe size={25} color="#426CFF" variant="Bulk" />} 
              label="Security orchestration" 
              isDisabled={true} 
              isExpanded={isExpanded} 
            />
            
            <NavItem 
              icon={<SecuritySafe size={25} color="#426CFF" variant="Bulk" />} 
              label="Case management" 
              isDisabled={true} 
              isExpanded={isExpanded} 
            />
            
            <NavItem 
              icon={<ShieldSearch size={25} color="#426CFF" variant="Bulk" />} 
              label="Threat Intelligence" 
              externalUrl={Threat_Intelligence}
              isDisabled={true}
              isExpanded={isExpanded} 
            />
            
            <NavItem 
              icon={<Fatrows size={25} color="#426CFF" variant="Bulk" />} 
              label="Report" 
              isDisabled={true} 
              isExpanded={isExpanded} 
            />
            
            {/* Administration Section - Replaced with direct nav item */}
            <NavItem 
              icon={<KeySquare size={25} color="#426CFF" variant="Bulk" />} 
              label="Administration" 
              path="/dashboard" 
              isActive={isLinkActive('/dashboard')}
              isExpanded={isExpanded}
              onClick={toggleAdminPanel}
            />
          </div>
        </div>
        
        {/* User Profile */}
        <div className="p-4">
          {isExpanded ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-background-hover flex items-center justify-center text-white font-medium">
                  {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">
                    {user?.first_name && user?.last_name 
                      ? `${user.first_name} ${user.last_name}` 
                      : user?.username || 'User'
                    }
                  </p>
                  <p className="text-xs text-gray-400">
                    {user?.role || 'user'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={onOpenSettings}
                  className="p-1.5 rounded-full hover:bg-background-hover text-gray-400"
                  title="Settings"
                >
                  <Setting2 size={20} color="#426CFF" variant="Bulk" />
                </button>
                <button 
                  onClick={handleLogout}
                  className="p-1.5 rounded-full hover:bg-background-hover text-gray-400"
                  title="Logout"
                >
                  <LogoutCurve size={20} color="#426CFF" variant="Bulk" className="transform rotate-180" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-4">
              <div className="w-10 h-10 rounded-full bg-background-hover flex items-center justify-center text-white font-medium">
                {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
              </div>
              <div className="flex flex-col items-center space-y-2">
                <button 
                  onClick={onOpenSettings}
                  className="p-1.5 rounded-full hover:bg-background-hover text-gray-400"
                  title="Settings"
                >
                  <Setting2 size={20} color="#426CFF" variant="Bulk" />
                </button>
                <button 
                  onClick={handleLogout}
                  className="p-1.5 rounded-full hover:bg-background-hover text-gray-400"
                  title="Logout"
                >
                  <LogoutCurve size={20} color="#426CFF" variant="Bulk" className="transform rotate-180" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Side Panel for Administration */}
      <SidePanel 
        isVisible={showAdminPanel}
        topPosition={adminButtonPosition - 100}
      >
        <div className="space-y-2">
          <NavItem 
            icon={<Element4 size={20} color="#426CFF" variant="Bulk" />} 
            label="Dashboard" 
            path="/dashboard" 
            isActive={isLinkActive('/dashboard')}
            onClick={handleSubMenuItemClick}
          />
          
          {canAccess('tenants') && (
            <NavItem 
              icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
              label="Onboarding" 
              path="/tenants?level=L3&type=org" 
              isActive={isLinkActive('/tenants')}
              onClick={handleSubMenuItemClick}
            />
          )}
          
          {canAccess('products') && (
            <NavItem 
              icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
              label="Products" 
              path="/products" 
              isActive={isLinkActive('/products')}
              onClick={handleSubMenuItemClick}
            />
          )}
          
          {canAccess('integrations') && (
            <NavItem 
              icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
              label="Integrations" 
              path="/integrations" 
              isActive={isLinkActive('/integrate-product')}
              onClick={handleSubMenuItemClick}
            />
          )}

          {canAccess('forwarding') && (
          <NavItem 
            icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
            label="Forwarding" 
            path="/forwarding" 
            isActive={isLinkActive('/forwarding')}
            onClick={handleSubMenuItemClick}
          />
          )}
          
          {canAccess('audit_logs') && (
            <NavItem 
              icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
              label="Audit Logs" 
              path="/audit-logs" 
              isActive={isLinkActive('/audit-logs')}
              onClick={handleSubMenuItemClick}
            />
          )}
          
          {canAccess('rbac') && (
            <NavItem 
              icon={<SecuritySafe size={20} color="#426CFF" variant="Bulk" />} 
              label="Access Control" 
              path="/access-control" 
              isActive={isLinkActive('/access-control')}
              onClick={handleSubMenuItemClick}
            />
          )}
          
          {canAccess('changes') && (
            <NavItem 
              icon={<Task size={20} color="#426CFF" variant="Bulk" />} 
              label="Change Management" 
              path="/change-management" 
              isActive={isLinkActive('/change-management') || isLinkActive('/changes')}
              onClick={handleSubMenuItemClick}
            />
          )}
        </div>
      </SidePanel>

    </div>
  );
};

export default Sidebar;